import { useState } from 'react';
import { Button } from '@/components/ui/button';

interface Referral {
  referral_id: number;
  referred_email: string;
  created_at: string;
  resend_count: number;
}

interface ReferralData {
  email: string;
  total_pending: number;
  total_active: number;
  pending_referrals: Referral[];
  active_referrals: Referral[];
}

interface PendingEmailsSectionProps {
  referralData: ReferralData | null;
  onResendActivation: (referral: Referral) => void;
}

export const PendingEmailsSection = ({ referralData, onResendActivation }: PendingEmailsSectionProps) => {
  const [showPendingEmails, setShowPendingEmails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const emailsPerPage = 5;

  const totalPages = referralData?.pending_referrals 
    ? Math.ceil(referralData.pending_referrals.length / emailsPerPage) 
    : 0;

  const paginatedEmails = referralData?.pending_referrals 
    ? referralData.pending_referrals.slice(
        (currentPage - 1) * emailsPerPage,
        currentPage * emailsPerPage
      )
    : [];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const resetPagination = () => {
    setCurrentPage(1);
  };

  if (!referralData?.pending_referrals || referralData.pending_referrals.length === 0) {
    return null;
  }

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
      <button
        onClick={() => {
          setShowPendingEmails(!showPendingEmails);
          if (!showPendingEmails) {
            resetPagination();
          }
        }}
        className="flex items-center justify-between w-full p-4 bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#FFD200] rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="text-left">
            <h4 className="font-semibold text-gray-900 dark:text-white text-sm">Emails Pendentes</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">{referralData.pending_referrals.length} aguardando ativação</p>
          </div>
        </div>
        <svg 
          className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${showPendingEmails ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {showPendingEmails && (
        <div className="mt-4 space-y-3">
          {paginatedEmails.map((referral) => (
            <div 
              key={referral.referral_id} 
              className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 hover:border-[#FFD200] transition-colors duration-200"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-6 h-6 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-gray-700 dark:text-gray-300 text-xs font-medium">
                        {referral.referred_email[0].toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {referral.referred_email}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 text-xs text-gray-500 dark:text-gray-400 ml-9">
                    <span>
                      {new Date(referral.created_at).toLocaleDateString('pt-BR')}
                    </span>
                    <span>
                      Reenvios: {referral.resend_count}/2
                    </span>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <Button
                    size="sm"
                    onClick={() => onResendActivation(referral)}
                    disabled={referral.resend_count >= 2}
                    className={`text-xs w-full sm:w-auto px-4 py-2 ${
                      referral.resend_count >= 2 
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400' 
                        : 'bg-[#FFD200] hover:bg-[#FFB800] text-gray-900 font-medium'
                    }`}
                  >
                    {referral.resend_count >= 2 ? 'Limite atingido' : 'Reenviar'}
                  </Button>
                </div>
              </div>
            </div>
          ))}
          
          {/* Controles de Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <span className="text-sm font-medium text-gray-900 dark:text-white px-3 py-1 bg-[#FFD200] rounded">
                {currentPage} de {totalPages}
              </span>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};